import React, { FC } from 'react';
import { useTheme } from '@mui/material';
import moment from 'moment-timezone';

import { ITimecardSummaryDTO } from 'src/api/dtos';
import { HorizontalBarChart } from 'src/shared/components';
import { DATE_FORMAT } from 'src/shared/constants';
import { DurationFormatEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { formatDuration, mapTimecardCategoryDarkColors, mapTimecardCategoryLightColors } from 'src/shared/utils';

interface Props {
  currentSummary: ITimecardSummaryDTO | null;
  subtitle: string;
}

export const TimecardMetrics: FC<Props> = ({ currentSummary, subtitle }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const mapper = theme.palette.mode === 'light' ? mapTimecardCategoryLightColors : mapTimecardCategoryDarkColors;

  return (
    <div className="ms-3 mt-3 pt-2">
      <div className="d-flex align-items-center flex-wrap">
        <p className="mb-0 fw-400" style={{ minWidth: '40px' }}>
          {formatDuration(currentSummary?.totalHours, DurationFormatEnums.T_24_HOURS)}
        </p>
        <div className="d-flex align-items-end flex-grow-1 ms-0 ms-md-3">
          <div>
            <span className="small text-muted d-block">{t('global.total')}</span>
            <span className="small text-muted d-block text-capitalize">
              {t('timecards.paidHours')} - {subtitle}
            </span>
          </div>
          <span className="ms-1 small text-muted d-block">
            ({moment(currentSummary?.startDate).format(DATE_FORMAT)} -{' '}
            {moment(currentSummary?.endDate).format(DATE_FORMAT)})
          </span>
        </div>
      </div>
      <div className="mt-4">
        {(!currentSummary?.items.length || currentSummary.items.every((value) => !value)) && (
          <HorizontalBarChart value={0.5} />
        )}
        {currentSummary?.items
          .filter((item) => !item.isMemo)
          .map((item, index) => {
            return (
              <HorizontalBarChart
                key={item.timeCategoryId}
                color={mapper[index]}
                variant="dark"
                label={item.timeCategoryDescription}
                extraLabel={formatDuration(item.hours, DurationFormatEnums.T_24_HOURS)}
                value={getValue(+item.hours, +currentSummary.totalHours)}
              />
            );
          })}
      </div>
    </div>
  );
};

const getValue = (duration: number, total: number) => (duration / total) * 100;
